import { ErrorMessageSchema } from "@common/schemas/error.schema";
import {
    createSendingDomainSchema,
    listSendingDomainsResponseSchema,
    sendingDomainResponseSchema,
    updateSendingDomainSchema,
} from "@common/schemas/sending-domain.schema";
import {
    createSendingEmailSchema,
    listSendingEmailsResponseSchema,
    sendingEmailResponseSchema,
    updateSendingEmailSchema,
} from "@common/schemas/sending-email.schema";
import { initContract } from "@ts-rest/core";
import { z } from "zod";

const c = initContract();

const domainResponses = {
    create: {
        201: sendingDomainResponseSchema,
        400: ErrorMessageSchema,
        404: ErrorMessageSchema,
        409: ErrorMessageSchema,
        429: ErrorMessageSchema,
    },
    verify: {
        200: sendingDomainResponseSchema,
        400: ErrorMessageSchema,
        404: ErrorMessageSchema,
        429: ErrorMessageSchema,
    },
    update: {
        200: sendingDomainResponseSchema,
        400: ErrorMessageSchema,
        404: ErrorMessageSchema,
        409: ErrorMessageSchema,
        429: ErrorMessageSchema,
    },
    emailCreate: {
        201: sendingEmailResponseSchema,
        400: ErrorMessageSchema,
        404: ErrorMessageSchema,
        409: ErrorMessageSchema,
    },
    emailUpdate: {
        200: sendingEmailResponseSchema,
        400: ErrorMessageSchema,
        404: ErrorMessageSchema,
        409: ErrorMessageSchema,
    },
} as const;

/**
 * Both routers are identical apart from the owner id path-param key
 * (`workspaceId` vs `organizationId`) and the path prefix. Build them from a
 * single definition so the two cannot drift. The owner shape is passed in
 * (rather than a bare key string) so each router keeps its precise path-param
 * types for consumers.
 */
function buildSendingDomainRouter<OwnerShape extends z.ZodRawShape>(
    ownerShape: OwnerShape,
    pathPrefix: string,
) {
    const ownerParams = z.object(ownerShape);
    const domainParams = ownerParams.extend({ domainId: z.string().uuid() });
    const emailParams = ownerParams.extend({ emailId: z.string().uuid() });
    const idPath = `/:${Object.keys(ownerShape)[0]}`;

    return c.router(
        {
            listDomains: {
                method: "GET",
                path: idPath,
                pathParams: ownerParams,
                responses: {
                    200: listSendingDomainsResponseSchema,
                    404: ErrorMessageSchema,
                },
            },
            createDomain: {
                method: "POST",
                path: idPath,
                pathParams: ownerParams,
                body: createSendingDomainSchema,
                responses: domainResponses.create,
            },
            verifyDomain: {
                method: "POST",
                path: `${idPath}/:domainId/verify`,
                pathParams: domainParams,
                body: c.noBody(),
                responses: domainResponses.verify,
            },
            updateDomain: {
                method: "PATCH",
                path: `${idPath}/:domainId`,
                pathParams: domainParams,
                body: updateSendingDomainSchema,
                responses: domainResponses.update,
            },
            deleteDomain: {
                method: "DELETE",
                path: `${idPath}/:domainId`,
                pathParams: domainParams,
                body: c.noBody(),
                responses: { 204: c.noBody(), 404: ErrorMessageSchema },
            },
            listAllEmails: {
                method: "GET",
                path: `${idPath}/emails`,
                pathParams: ownerParams,
                responses: {
                    200: listSendingEmailsResponseSchema,
                    404: ErrorMessageSchema,
                },
            },
            listEmails: {
                method: "GET",
                path: `${idPath}/:domainId/emails`,
                pathParams: domainParams,
                responses: {
                    200: listSendingEmailsResponseSchema,
                    404: ErrorMessageSchema,
                },
            },
            createEmail: {
                method: "POST",
                path: `${idPath}/:domainId/emails`,
                pathParams: domainParams,
                body: createSendingEmailSchema,
                responses: domainResponses.emailCreate,
            },
            updateEmail: {
                method: "PATCH",
                path: `${idPath}/emails/:emailId`,
                pathParams: emailParams,
                body: updateSendingEmailSchema,
                responses: domainResponses.emailUpdate,
            },
            deleteEmail: {
                method: "DELETE",
                path: `${idPath}/emails/:emailId`,
                pathParams: emailParams,
                body: c.noBody(),
                responses: { 204: c.noBody(), 404: ErrorMessageSchema },
            },
        },
        { pathPrefix },
    );
}

export const sendingDomainContract = buildSendingDomainRouter(
    { workspaceId: z.string().uuid() },
    "/sending-domains",
);

export const organizationSendingDomainContract = buildSendingDomainRouter(
    { organizationId: z.string().uuid() },
    "/organization-sending-domains",
);
